// The Hijri calendar: a Gregorian year with each day's Hijri date (with the
// masjid's saved adjustment), today, Ramadan and the main Islamic dates, and
// the adjustment itself (previewed on change, saved on request).
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment/moment';
import toast from 'react-hot-toast';
import { useLazyGetHijriCalendarQuery } from '../../../store/Reducer/hijri-calendar';
import CalanderFilters from './calander-filters';
import CalanderSkelton from './calander-skelton';

const WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = moment.months();

// Widely shared Islamic dates, by Hijri month and day, so they follow the adjustment.
const KEY_DATES = [
    { month: 1, day: 1, name: 'Islamic New Year' },
    { month: 1, day: 10, name: 'Day of Ashura' },
    { month: 7, day: 27, name: "Isra and Mi'raj" },
    { month: 9, day: 1, name: 'First day of Ramadan' },
    { month: 10, day: 1, name: 'Eid al-Fitr' },
    { month: 12, day: 9, name: 'Day of Arafah' },
    { month: 12, day: 10, name: 'Eid al-Adha' },
];
const keyDateOf = (h) => KEY_DATES.find((k) => k.month === h.month && k.day === h.day);

// One aladhan day -> what the calendar needs.
const toDay = (item) => {
    const g = moment(item.gregorian.date, 'DD-MM-YYYY');
    const hijri = {
        day: Number(item.hijri.day),
        month: Number(item.hijri.month.number),
        monthName: item.hijri.month.en,
        monthAr: item.hijri.month.ar,
        year: item.hijri.year,
    };
    return { date: g, key: item.gregorian.date, hijri, keyDate: keyDateOf(hijri) };
};
const hijriText = (h) => `${h.day} ${h.monthName} ${h.year} AH`;

const Legend = () => (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-emerald-600" />Today</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-100 ring-1 ring-amber-300" />Ramadan</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-rose-100 ring-1 ring-rose-300" />Islamic date</span>
        <span className="inline-flex items-center gap-1.5"><span className="rounded bg-emerald-100 px-1 text-[10px] font-semibold text-emerald-800">1</span>Start of a Hijri month</span>
        <span className="inline-flex items-center gap-1.5"><span className="font-semibold text-emerald-700">Fri</span>Jumu'ah</span>
    </div>
);

const MonthCard = ({ name, days, isThisMonth, todayKey }) => {
    const first = days[0];
    const last = days[days.length - 1];
    const hijriRange = first && last
        ? (first.hijri.monthName === last.hijri.monthName
            ? `${first.hijri.monthName} ${first.hijri.year}`
            : `${first.hijri.monthName} – ${last.hijri.monthName} ${last.hijri.year}`)
        : '';
    const offset = first ? WEEK.indexOf(first.date.format('dddd')) : 0;

    return (
        <section
            id={`month-${name}`}
            aria-label={name}
            className={`scroll-mt-24 rounded-2xl bg-white p-4 shadow-sm border ${isThisMonth ? 'border-emerald-400 ring-2 ring-emerald-100' : 'border-gray-200'}`}
        >
            <div className="mb-3 flex items-baseline justify-between gap-2">
                <h3 className="text-lg font-semibold text-gray-900">{name}</h3>
                <p className="text-right text-xs text-gray-500">{hijriRange}</p>
            </div>
            <div className="grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide">
                {WEEK.map((d) => (
                    <div key={d} className={`pb-1 ${d === 'Friday' ? 'text-emerald-700' : 'text-gray-400'}`}>{d.slice(0, 3)}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
                {days.map((d, i) => {
                    const isToday = d.key === todayKey;
                    const isRamadan = d.hijri.month === 9;
                    const startsMonth = d.hijri.day === 1;
                    const tone = isToday
                        ? 'bg-emerald-600 text-white shadow'
                        : d.keyDate
                            ? 'bg-rose-50 text-rose-900 ring-1 ring-rose-200'
                            : isRamadan
                                ? 'bg-amber-50 text-amber-900'
                                : 'text-gray-800 hover:bg-gray-50';
                    const label = `${d.date.format('dddd D MMMM YYYY')} · ${hijriText(d.hijri)}${d.keyDate ? ` · ${d.keyDate.name}` : ''}${isToday ? ' · Today' : ''}`;
                    return (
                        <div
                            key={d.key}
                            title={label}
                            aria-label={label}
                            aria-current={isToday ? 'date' : undefined}
                            className={`flex h-11 sm:h-12 flex-col items-center justify-center rounded-lg leading-tight ${tone}`}
                            style={i === 0 ? { gridColumnStart: offset + 1 } : undefined}
                        >
                            <span className="text-sm font-semibold">{d.date.date()}</span>
                            <span
                                className={`text-[11px] ${startsMonth && !isToday
                                    ? 'rounded bg-emerald-100 px-1 font-semibold text-emerald-800'
                                    : isToday ? 'text-emerald-50' : 'text-gray-500'}`}
                            >
                                {d.hijri.day}
                            </span>
                        </div>
                    );
                })}
            </div>
            {days.some((d) => d.keyDate) && (
                <ul className="mt-3 space-y-1 border-t border-gray-100 pt-2 text-xs text-rose-800">
                    {days.filter((d) => d.keyDate).map((d) => (
                        <li key={d.key}><span className="font-semibold">{d.date.date()}</span> · {d.keyDate.name}</li>
                    ))}
                </ul>
            )}
        </section>
    );
};

const Calendar = () => {
    const now = moment();
    const todayKey = now.format('DD-MM-YYYY');
    const [year, setYear] = useState(now.year());
    const [saved, setSaved] = useState(null); // the masjid's saved adjustment
    const [variation, setVariation] = useState(0); // what the calendar shows
    const [saving, setSaving] = useState(false);

    const [loadYear, { data, isFetching }] = useLazyGetHijriCalendarQuery();
    // Next year with the saved adjustment, for "Coming up" near the year's end.
    const [loadNextYear, { data: nextYearData }] = useLazyGetHijriCalendarQuery();

    // First load: this year with the saved adjustment.
    useEffect(() => {
        loadYear({ year: now.year(), mode: 'get' })
            .unwrap()
            .then((res) => {
                setSaved(Number(res?.variation) || 0);
                setVariation(Number(res?.variation) || 0);
            })
            .catch((error) => toast.error(error?.data?.message || "Couldn't load the Hijri calendar."));
        loadNextYear({ year: now.year() + 1, mode: 'get' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A new year or adjustment reloads the calendar (after the first load).
    useEffect(() => {
        if (saved === null || year < 1900 || year > 2100) return undefined;
        const timer = setTimeout(() => {
            loadYear({ year, variation, mode: 'view' })
                .unwrap()
                .catch((error) => toast.error(error?.data?.message || "Couldn't load the Hijri calendar."));
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, variation]);

    const days = useMemo(() => (data?.data || []).map(toDay), [data]);
    const byMonth = useMemo(() => {
        const out = MONTHS.map(() => []);
        for (const d of days) out[d.date.month()].push(d);
        return out;
    }, [days]);
    const shownYear = days[0]?.date.year();
    const today = days.find((d) => d.key === todayKey);
    // This year with the saved adjustment stays known while another year or
    // an unsaved adjustment is shown: it gives "Today" and "Coming up".
    const [thisYear, setThisYear] = useState([]);
    useEffect(() => {
        if (shownYear === now.year() && variation === saved && days.length) setThisYear(days);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);
    const todayShown = today || thisYear.find((d) => d.key === todayKey);

    const upcoming = useMemo(
        () => [...thisYear, ...(nextYearData?.data || []).map(toDay)]
            .filter((d) => d.keyDate && d.date.isSameOrAfter(now, 'day'))
            .slice(0, 3),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [thisYear, nextYearData]
    );

    const save = async () => {
        setSaving(true);
        try {
            await loadYear({ year, variation, mode: 'save' }).unwrap();
            setSaved(variation);
            toast.success('Hijri adjustment saved. Your website and screens now use it.');
        } catch (error) {
            toast.error(error?.data?.message || "Couldn't save the adjustment. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const goToThisMonth = () => {
        setYear(now.year());
        setTimeout(() => document.getElementById(`month-${MONTHS[now.month()]}`)?.scrollIntoView({ behavior: 'smooth' }), 400);
    };

    return (
        <div className="space-y-6">
            <div className="grid gap-4 lg:grid-cols-3">
                <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 p-5 text-white shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-100">Today</p>
                    {todayShown ? (
                        <>
                            <p className="mt-2 text-3xl font-bold">{todayShown.hijri.day} {todayShown.hijri.monthName}</p>
                            <p className="text-lg text-emerald-50">{todayShown.hijri.year} AH <span className="ml-2 font-normal" lang="ar" dir="rtl">{todayShown.hijri.monthAr}</span></p>
                            <p className="mt-3 text-sm text-emerald-100">{todayShown.date.format('dddd D MMMM YYYY')}</p>
                            {variation !== saved && today && (
                                <p className="mt-2 inline-block rounded-full bg-white/15 px-2 py-0.5 text-xs">Preview with the new adjustment</p>
                            )}
                        </>
                    ) : (
                        <div className="mt-3 space-y-2">
                            <div className="h-8 w-48 animate-pulse rounded bg-white/20" />
                            <div className="h-4 w-32 animate-pulse rounded bg-white/20" />
                        </div>
                    )}
                </div>

                <CalanderFilters
                    variation={variation}
                    saved={saved}
                    onVariation={setVariation}
                    onSave={save}
                    onReset={() => setVariation(saved)}
                    saving={saving}
                />

                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Coming up</p>
                    {upcoming.length ? (
                        <ul className="mt-3 space-y-3">
                            {upcoming.map((d, i) => {
                                const inDays = d.date.diff(now.clone().startOf('day'), 'days');
                                return (
                                    <li key={d.key} className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className={`font-semibold ${i === 0 ? 'text-gray-900' : 'text-gray-700'}`}>{d.keyDate.name}</p>
                                            <p className="text-xs text-gray-500">{d.date.format('ddd D MMM YYYY')} · {d.hijri.day} {d.hijri.monthName}</p>
                                        </div>
                                        <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">
                                            {inDays === 0 ? 'Today' : inDays === 1 ? 'Tomorrow' : `in ${inDays} days`}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="mt-3 text-sm text-gray-500">Loading…</p>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setYear((y) => y - 1)} aria-label="Previous year"
                        className="h-10 w-10 rounded-lg border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">‹</button>
                    <label htmlFor="year" className="sr-only">Year</label>
                    <input
                        id="year"
                        type="number"
                        min="1900"
                        max="2100"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        className="h-10 w-24 rounded-lg border border-gray-300 text-center text-base font-semibold text-gray-900 [-moz-appearance:_textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <button type="button" onClick={() => setYear((y) => y + 1)} aria-label="Next year"
                        className="h-10 w-10 rounded-lg border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">›</button>
                    <button type="button" onClick={goToThisMonth}
                        className="ml-1 h-10 rounded-lg px-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50">This month</button>
                </div>
                <Legend />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {MONTHS.map((name, i) =>
                    isFetching || !byMonth[i].length ? (
                        <CalanderSkelton key={name} />
                    ) : (
                        <MonthCard
                            key={name}
                            name={name}
                            days={byMonth[i]}
                            todayKey={todayKey}
                            isThisMonth={shownYear === now.year() && i === now.month()}
                        />
                    )
                )}
            </div>
        </div>
    );
};

export default Calendar;
